<template>
  <base-login-box :title="$t('login.welcome')" :subtitle="$t('login.enter_details')">
    <VeeForm as="form" @submit="handleLogin">
      <VeeField v-slot="{field, errorMessage, setErrors, meta}"
                v-model="email"
                :name="$t('login.email')"
                rules="email|required">
        <bf-input :model-value="field.value"
                  class="relative"
                  type="email"
                  :label="$t('login.email')"
                  :placeholder="$t('login.email_placeholder')"
                  autofocus
                  size="lg"
                  :validation-status="!errorMessage && !loginErrorMessage ? '' : 'error'"
                  @update:model-value="field.onInput"
                  @input="handleInput(field, meta, setErrors)"
                  @blur="field.onBlur">
          <template #validationMessage>
            {{ errorMessage }}
          </template>
        </bf-input>
      </VeeField>
      <VeeField v-slot="{field, errorMessage, setErrors, meta}"
                v-model="password"
                :name="$t('login.password')"
                rules="required">
        <bf-input :model-value="field.value"
                  class="relative"
                  :label="$t('login.password')"
                  size="lg"
                  :placeholder="$t('login.password_placeholder')"
                  :type="showingPassword ? 'text' : 'password'"
                  :validation-status="!errorMessage && !loginErrorMessage ? '' : 'error'"
                  @update:model-value="field.onInput"
                  @input="handleInput(field, meta, setErrors)"
                  @blur="field.onBlur">
          <template #validationMessage>
            {{ loginErrorMessage || errorMessage }}
          </template>
          <template #suffix>
            <div class="text-text-secondary bg-transparent focus:outline-none font-medium text-sm p-2 cursor-pointer"
                 tabindex="-1"
                 @click.prevent="showingPassword = !showingPassword">
              <font-awesome-icon v-if="showingPassword" :icon="['far', 'eye']"/>
              <font-awesome-icon v-else :icon="['far', 'eye-slash']"/>
            </div>

          </template>
        </bf-input>
      </VeeField>
      <div class="mb-10 flex items-center justify-between">
        <bf-checkbox
            v-model="rememberMe"
            :label="$t('login.remember_me')"
            data-testid="remember-me"
            name="remember-me"/>
        <bf-a class="text-sm text-blue-700 font-medium"
              tabindex="-1"
              @click.prevent="goToForgotPassword">
          {{ $t('login.forgot_password') }}
        </bf-a>
      </div>

      <div class="mt-12">
        <bf-button type="submit"
                   :executing-label="$t('login.logging_in')"
                   class="w-full items-center justify-center"
                   :executing="loading">
          {{ $t('login.login') }}
        </bf-button>
      </div>
    </VeeForm>
  </base-login-box>
</template>

<script setup lang="ts">
import BaseLoginBox from "~/components/login/BaseLoginBox.vue";
import {useUserStore} from "~/store/user";
import {navigateTo, useRoute} from "#app";
import {$confirm} from '~/composables/useConfirm';
import {isDesktopBrowser} from '~/utils/mobileBrowser';

import {ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import BfInput from "~/components/global/BfInput.vue";
import type {FieldBindingObject, FieldMeta} from "vee-validate"; // Import useI18n

const route = useRoute();
const runtimeConfig = useRuntimeConfig();

// Set up refs for form data
const userStore = useUserStore();
const email = ref('');
const password = ref('');
const rememberMe = ref(false);
const loading = ref(false);
const showingPassword = ref(false);
const loginErrorMessage = ref('');
const {t} = useI18n();
const redirectUrl = route.query.redirect as string;
const desktopWarningActive = ref<boolean>(false);

const goToForgotPassword = () => {
  navigateTo('/auth/forgot-password');
}
onMounted(() => {
  const desktopAppUrl = runtimeConfig.public.desktopAppUrl?.trim();
  if (!isDesktopBrowser() || !desktopAppUrl || desktopWarningActive.value)
    return;
  desktopWarningActive.value = true;

  $confirm.success(
    t('general.desktop_app_warning.title'),
    t('general.desktop_app_warning.message'),
    {
      type: 'info',
      confirm: {
        text: t('button.desktop_app_warning.open_desktop_version'),
        visible: true,
        color: 'primary'
      },
      cancel: {
        text: t('button.desktop_app_warning.stay_on_mobile'),
        color: 'outline',
        visible: true
      }
    },
    'desktop'
  ).then(() => {
    void navigateTo(desktopAppUrl, {external: true});
  }).catch(() => {});
});
// Define the function to handle form submission
const handleLogin = async () => {
  if (!isFormValid.value) {
    return;
  }
  loading.value = true;

  try {
    const response = await userStore.login(email.value, password.value, rememberMe.value);
    // Handle success, e.g., redirect to the dashboard or show a success message
    // Upon success redirect the user to the index.vue page

    if (response.success && !response.twoFactorRequired) {
      if (userStore.user.twoFactorMandatory) {
        return navigateTo({name: 'auth-enable-mfa', query: {redirect: redirectUrl}});
      } else if (redirectUrl) {
        return navigateTo(redirectUrl);
      } else {
        return navigateTo('/documents/inbox');
      }
    }
    if (response.success && response.twoFactorRequired) {
      return navigateTo({path: '/login-mfa', query: {redirect: redirectUrl}});
    }

    // loading.value = false;
  } catch {
    // Handle error, e.g., show an error message
    loginErrorMessage.value = t('login.errors.invalid_credentials');
    password.value = '';
    loading.value = false;
  }
};

const handleInput = (field: FieldBindingObject, meta: FieldMeta<string>, setErrors: (errors: string[]) => void) => {
  if (loginErrorMessage.value)
    loginErrorMessage.value = '';

  if (meta.dirty) { // Ensures that clearing happens only if the meta was once blurred
    setErrors([]); // Resets validation state of the field
  }
};

// create a computed property to ensure both email and password is filled
const isFormValid = computed(() => {
  // If the email matches the email regex and the password is at least 6 characters long, the form is valid
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return email.value.match(emailRegex) && password.value;
});
</script>